import { Controller } from '@hotwired/stimulus';

interface GrowableGrid {
  gatherRows(): Record<string, string>[];
}

interface ValidateResponse {
  valid: boolean;
}

const DISABLED_CLASS = 'toolkit-button-component--disabled';
const VALIDATING_LABEL = 'Validating rows, please wait...';
const ERROR_MESSAGE =
  'There are errors in your list that must be fixed before continuing. To view the errors and explanations, click Download List. Then, fix the errors, and verify by selecting Validate List again.';

export default class extends Controller<HTMLElement> {
  static targets = ['grid', 'validateButton', 'downloadButton', 'updateButton', 'notices'];

  static values = {
    validateUrl: String,
    columns: Array,
    growableId: String,
  };

  declare readonly gridTarget: HTMLElement;
  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly downloadButtonTarget: HTMLElement;
  declare readonly updateButtonTarget: HTMLElement;
  declare readonly noticesTarget: HTMLElement;

  declare readonly validateUrlValue: string;
  declare readonly columnsValue: string[];
  declare readonly growableIdValue: string;

  private validated = false;

  async validate(event: Event) {
    event.preventDefault();

    const grid = this.growableGrid();
    if (!grid) return;

    const button = this.validateButtonTarget;
    const originalLabel = button.innerHTML;
    button.disabled = true;
    button.textContent = VALIDATING_LABEL;

    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: this.buildPayload(grid.gatherRows()),
      });
      if (!response.ok) throw new Error(`Validate request failed: ${response.status}`);

      this.showResult(((await response.json()) as ValidateResponse).valid);
    } catch {
      this.showNotice('inline_error', 'Validation could not be completed. Please try again.');
      button.disabled = false;
    } finally {
      button.innerHTML = originalLabel;
    }
  }

  // An edit after a successful validation makes the server-cached list stale.
  handleGridChanged() {
    if (!this.validated) return;

    this.validated = false;
    this.validateButtonTarget.disabled = false;
    this.setUpdateEnabled(false);
    this.noticesTarget.replaceChildren();
  }

  handleDownload() {
    this.downloadButtonTarget.classList.add('hide');
    this.noticesTarget.replaceChildren();
  }

  guardUpdate(event: Event) {
    if (!this.updateButtonTarget.classList.contains(DISABLED_CLASS)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  // Same shape the legacy Handsontable page sent via jQuery: `headers[col]=` per column
  // (order matters) and `rows[i][]=value` per cell, blanks as empty strings.
  private buildPayload(rows: Record<string, string>[]): URLSearchParams {
    const body = new URLSearchParams();
    this.columnsValue.forEach((column) => body.append(`headers[${column}]`, ''));
    rows.forEach((row, index) => {
      this.columnsValue.forEach((column) => body.append(`rows[${index}][]`, row[column] ?? ''));
    });
    return body;
  }

  private showResult(valid: boolean) {
    this.validated = valid;
    this.downloadButtonTarget.classList.toggle('hide', valid);
    this.setUpdateEnabled(valid);
    this.validateButtonTarget.disabled = valid;

    if (valid) {
      this.showNotice('inline_success', 'Everything looks good!');
    } else {
      this.showNotice('inline_error', ERROR_MESSAGE);
    }
  }

  private showNotice(className: string, message: string) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = message;
    this.noticesTarget.replaceChildren(span);
  }

  private setUpdateEnabled(enabled: boolean) {
    this.updateButtonTarget.classList.toggle(DISABLED_CLASS, !enabled);
    this.updateButtonTarget.setAttribute('aria-disabled', String(!enabled));
  }

  private growableGrid(): GrowableGrid | null {
    const el = this.gridTarget.querySelector<HTMLElement>(`[data-controller~="${this.growableIdValue}"]`);
    if (!el) return null;

    return this.application.getControllerForElementAndIdentifier(
      el,
      this.growableIdValue
    ) as unknown as GrowableGrid | null;
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
  }
}
